// Document metadata types — shared between client (form rendering) and server (XML mapping)
// These define what fields/flags the UI must collect per document type.
/**
 * Filer-facing display-name overrides for metadata codes whose policy `name`
 * reads poorly to a filer (SOR-164). The single source of truth for the copy
 * every surface shows — the field label, the placeholder, the group heading,
 * AND the validation message ("<doc>" — <name> is required). Keyed by code and
 * DATA-ONLY: presentation only, never court- or vendor-conditional. The
 * golden-gated JTI envelope consumes these codes by CODE, never by name
 * (stage-3-envelope.ts), so overriding the name here cannot move an envelope.
 *
 * History: SOR-157 renamed the "Other" document code's field label to
 * "Document Name" as an inline override in DocumentMetadataForm; SOR-161 then
 * had to fix the placeholder and group heading the same inline way. Each was a
 * new local override, so each new surface reading the policy name regressed the
 * copy — the validation message (a fourth consumer, in this package) was the
 * one SOR-164 caught. Routing every surface through this one table ends the
 * class.
 */
const METADATA_DISPLAY_OVERRIDES = {
    ADDITIONAL_INFO: { name: 'Document Name' },
};
/**
 * Own-property lookup into the override table. `code` is vendor policy data, so
 * a plain `TABLE[code]` would reach Object.prototype and resolve codes like
 * `constructor` or `toString` to a truthy member — rendering "Object" as a
 * filer-facing name and (via getDisplayMetadata) clearing a real description.
 */
function lookupOverride(code) {
    // hasOwnProperty.call, not Object.hasOwn — this package's tsc target predates
    // ES2022, and the tsx test runner would happily run what the build rejects.
    return Object.prototype.hasOwnProperty.call(METADATA_DISPLAY_OVERRIDES, code)
        ? METADATA_DISPLAY_OVERRIDES[code]
        : undefined;
}
/**
 * The filer-facing display name for a metadata item — its override when one
 * exists, else its verbatim policy `name`. Every surface that shows a metadata
 * name to a filer (label, placeholder, group heading, validation message) reads
 * through here so the copy can never diverge across surfaces again (SOR-164).
 */
export function getMetadataDisplayName(item) {
    return lookupOverride(item.code)?.name ?? item.name;
}
/**
 * A display-ready copy of a metadata item: its name replaced by the filer-facing
 * override and its `description` cleared when an override applies. Clearing the
 * description is load-bearing (SOR-161): TextField's placeholder falls back to
 * the policy `description` when present, so leaving it would leak the old
 * wording ("Additional Info") back into the box the override renamed. An item
 * with no override is returned unchanged.
 */
export function getDisplayMetadata(item) {
    const override = lookupOverride(item.code);
    if (!override)
        return item;
    return { ...item, name: override.name, description: undefined };
}
